// Tron Legacy infinite grid — PlaneGeometry with TSL shader nodes

import { Mesh, PlaneGeometry, DoubleSide, Vector3 } from 'three/webgpu';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  Fn, uv, float, vec2, vec3, mix,
  fract, fwidth, abs, max, smoothstep, sin, clamp,
  positionWorld, cameraPosition, normalWorld,
  normalize, dot, pow, uniform,
} from 'three/tsl';
import type UniformNode from 'three/src/nodes/core/UniformNode.js';
import type { RenderCapabilities } from './detect-capabilities';
import { QUALITY_BUDGETS } from './detect-capabilities';

const NUM_RIPPLES = 6;
const RIPPLE_SPEED = 15.0;       // expansion speed (units/s)
const RIPPLE_MAX_AGE = 8.0;      // seconds before fully faded
const RIPPLE_RING_WIDTH = 8.0;   // visible ring thickness (units)
const RIPPLE_WAVE_FREQ = 1.5;    // sine frequency within the ring
const SPAWN_INTERVAL_MIN = 0.8;  // seconds between spawns
const SPAWN_INTERVAL_MAX = 2.0;
const GRID_HALF_EXTENT = 70;     // spawn within ±70 units

export interface TronGrid {
  mesh: Mesh;
  uTime: UniformNode<number>;
  spawnRipple: (x: number, z: number, time: number) => void;
  dispose: () => void;
}

export function createGrid(uTime: UniformNode<number>, caps: RenderCapabilities): TronGrid {
  const budget = QUALITY_BUDGETS[caps.qualityLevel];
  const geometry = new PlaneGeometry(200, 200, 1, 1);

  // Anti-aliased grid line function
  const gridLine = Fn(([coord_immutable, lineWidth_immutable]: [any, any]) => {
    const coord = float(coord_immutable);
    const lineWidth = float(lineWidth_immutable);
    const d = abs(fract(coord.sub(0.5)).sub(0.5));
    const aa = fwidth(coord);
    return float(1.0).sub(smoothstep(lineWidth.sub(aa), lineWidth.add(aa), d));
  });

  // Concentric ripple system — each ripple is a vec3 uniform (centerX, centerZ, birthTime)
  const rippleUniforms: UniformNode<Vector3>[] = [];
  for (let i = 0; i < NUM_RIPPLES; i++) {
    rippleUniforms.push(uniform(new Vector3(0, 0, -999)));
  }

  // Ripple spawning — triggered externally (e.g. when a light column fades out)
  let rippleIndex = 0;

  function spawnRipple(x: number, z: number, time: number): void {
    rippleUniforms[rippleIndex].value.set(x, z, time);
    rippleIndex = (rippleIndex + 1) % NUM_RIPPLES;
  }

  // Energy wave from concentric ripples expanding outward
  const energyWaveFn = Fn(() => {
    let total = float(0.0);

    for (let i = 0; i < NUM_RIPPLES; i++) {
      const center = vec2(rippleUniforms[i].x, rippleUniforms[i].y);
      const birth = rippleUniforms[i].z;
      const age = uTime.sub(birth);

      // Distance from this fragment to ripple center
      const dist = vec2(positionWorld.x, positionWorld.z).sub(center).length();

      // Expanding ring front
      const waveRadius = age.mul(RIPPLE_SPEED);
      const ringDist = abs(dist.sub(waveRadius));

      // Sine wave within the ring
      const wave = sin(ringDist.mul(RIPPLE_WAVE_FREQ)).mul(0.5).add(0.5);

      // Fade outside the ring width
      const ringFade = float(1.0).sub(smoothstep(float(0.0), float(RIPPLE_RING_WIDTH), ringDist));

      // Fade with age
      const ageFade = float(1.0).sub(smoothstep(float(0.0), float(RIPPLE_MAX_AGE), age));

      total = total.add(wave.mul(ringFade).mul(ageFade));
    }

    return clamp(total, float(0.0), float(1.0));
  });

  // Grid color: solid cyan
  const gridColor = vec3(0.0, 0.898, 1.0);  // #00E5FF

  // Grid opacity via TSL
  const gridShader = Fn(() => {
    const gridUV = uv();
    const scrollOffset = uTime.mul(2.0);

    // Major grid: spacing 10.0 (UV * 20 cells on 200-unit plane → 10 units each)
    const majorCoord = gridUV.mul(20.0);
    const majorX = gridLine(majorCoord.x, float(0.02));
    const majorY = gridLine(majorCoord.y.add(scrollOffset.div(10.0)), float(0.02));
    const majorGrid = max(majorX, majorY).mul(0.80);

    // Pulse: 0.5Hz sinusoidal on major lines, amplitude ±15% (skip for low quality)
    const majorWithPulse = budget.gridPulse
      ? majorGrid.mul(sin(uTime.mul(Math.PI)).mul(0.15).add(1.0))
      : majorGrid;

    // Minor grid: spacing 1.0 (UV * 200 cells on 200-unit plane → 1 unit each)
    let grid = majorWithPulse;
    if (budget.gridMinorLines) {
      const minorCoord = gridUV.mul(200.0);
      const minorX = gridLine(minorCoord.x, float(0.01));
      const minorY = gridLine(minorCoord.y.add(scrollOffset), float(0.01));
      const minorGrid = max(minorX, minorY).mul(0.30);
      grid = max(majorWithPulse, minorGrid);
    }

    // Energy ripples: boost grid lines only
    const rippleIntensity = energyWaveFn();
    const gridBoost = rippleIntensity.mul(0.8).mul(grid);
    const gridWithEnergy = grid.add(gridBoost);

    // Distance fade from world origin (camera is near origin)
    const dist = positionWorld.xz.length();
    const fade = float(1.0).sub(smoothstep(float(50.0), float(150.0), dist));

    return gridWithEnergy.mul(fade);
  });

  // Fresnel edge glow — brighter when viewed at grazing angles
  const fresnelShader = Fn(() => {
    const viewDir = normalize(cameraPosition.sub(positionWorld));
    const NdotV = abs(dot(normalWorld, viewDir));
    // power 3.0 (mid-range per spec), scale 0.8
    return pow(float(1.0).sub(NdotV), float(3.0)).mul(0.8);
  });

  const fresnelFactor = fresnelShader();

  const material = new MeshBasicNodeMaterial();
  // Energy flow color boosted by Fresnel: lerp toward bright cyan-white at edges
  const edgeColor = vec3(0.6, 1.0, 1.0);   // brighter cyan-white for edge glow
  const baseColor = mix(gridColor, edgeColor, fresnelFactor);
  // Ripples shift color toward amber
  const gridOpacity = gridShader();
  const rippleColorFactor = gridOpacity.mul(energyWaveFn());
  material.colorNode = mix(baseColor, vec3(1.0, 0.4, 0.0), rippleColorFactor);
  // Opacity: energy flow grid boosted at grazing angles for edge visibility
  material.opacityNode = gridOpacity.add(fresnelFactor.mul(gridOpacity).mul(0.5));
  material.transparent = true;
  material.side = DoubleSide;
  material.depthWrite = false;

  const mesh = new Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = -2;

  function dispose(): void {
    geometry.dispose();
    material.dispose();
  }

  return { mesh, uTime, spawnRipple, dispose };
}
